import type { MessageKey } from '@ralph/contracts'
import { formatCount, pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the marketplace domain's refusals say, in Brazilian Portuguese (checked against the
 * English shape at compile time).
 *
 * ── Gênero, e por que ele decide as frases ──
 *
 * *agente* é masculino, e quase toda frase aqui fala de um: por isso *este agente*, *pausado*,
 * *descontinuado*, *publicado*, *suspenso* — e os pronomes colados no verbo (*publicá-lo*,
 * *descontinuá-lo*, *trazê-lo de volta*, *movê-lo*). É exatamente por isso que o verbo nunca é
 * um parâmetro: em inglês "can ${what}" funciona, em português o objeto entra dentro do verbo
 * e a frase teria de ser remontada palavra a palavra (D-20).
 *
 * *denúncia* (f.) para report, *versão* (f.), *prévia* (f.), *pessoa* (f.), *grupo* (m.),
 * *base de conhecimento* (f.) — todos do ADR-0018.
 *
 * ── Os dois registros (AGM-022), que aqui são duas palavras ──
 *
 * Quem cria e quem usa lê *pausado* e *descontinuado*, que é o que as telas de marketplace já
 * dizem. Quem administra lê *suspenso* e *suspensão*, que é o que a tela de administração já
 * diz. Não é inconsistência: é a mesma distinção que o inglês faz entre "Retire" na tela do
 * criador e "Suspend" na tela do administrador, e ela já está ratificada nos dois catálogos.
 *
 * ── O que fica em inglês ──
 *
 * `official` e `verified` nas frases do lint são as palavras que a regra proíbe, não prosa:
 * traduzi-las descreveria uma regra que não existe. `id` também fica (D-17).
 */
export const marketplace: Pick<Messages['errors'], Extract<MessageKey, `marketplace.${string}`>> = {
  'marketplace.report_not_found': () => 'Essa denúncia não existe mais.',

  // --- identidade e alcance --------------------------------------------------

  // Indisponível e suspenso são `agents.*`: uma frase só para o produto inteiro.
  'marketplace.agent_no_longer_usable': () =>
    'Você não pode mais usar este agente, então não dá para enviar novas mensagens. Esta conversa continua legível.',
  'marketplace.agent_retired': () => 'Este agente foi descontinuado por quem é responsável por ele.',
  'marketplace.agent_retired_with_replacement': ({ replacement }) =>
    `Este agente foi descontinuado por quem é responsável por ele. “${replacement}” entra no lugar dele.`,
  'marketplace.agent_turned_off': () => 'Este agente foi desligado por um administrador.',

  // --- o construtor ----------------------------------------------------------

  'marketplace.agent_changed_while_editing': () =>
    'Outra pessoa salvou este agente enquanto você editava. Recarregue e refaça sua alteração.',
  'marketplace.agent_address_taken': () => 'Já existe um agente com esse endereço. Escolha outro.',
  'marketplace.agent_id_or_name_taken': () => 'Já existe um agente com esse nome ou endereço.',
  'marketplace.owner_must_be_you_or_a_managed_group': () =>
    'Você pode criar um agente para você ou para um grupo que você gerencia.',
  'marketplace.attachment_not_available': () =>
    'Uma dessas bases de conhecimento não está disponível para você. Recarregue a lista e escolha de novo.',
  'marketplace.preview_not_opened': () => 'Não foi possível abrir a prévia. Tente de novo.',
  'marketplace.version_not_found': ({ version }) => `Este agente não tem a versão ${version} para restaurar.`,
  /** Zero fica no singular em português, e um limite zerado recusa já na primeira prévia (D-19). */
  'marketplace.preview_budget_spent': ({ used }) =>
    pluralCategory('pt-BR', used) === 'one'
      ? 'Você testou um agente no construtor uma vez hoje, e é esse o número de prévias que uma pessoa pode executar por dia. Tente de novo amanhã, ou peça a um administrador para aumentar o limite.'
      : `Você testou agentes no construtor ${formatCount('pt-BR', used)} vezes hoje, e é esse o número de prévias que uma pessoa pode executar por dia. Tente de novo amanhã, ou peça a um administrador para aumentar o limite.`,

  // --- quem pode agir --------------------------------------------------------

  'marketplace.requires_maintainer': () =>
    'Só quem mantém este agente pode fazer isso. Peça a essas pessoas para fazer a alteração.',
  'marketplace.publish_requires_maintainer': () => 'Só quem mantém este agente pode publicá-lo.',
  'marketplace.unpublish_requires_maintainer': () => 'Só quem mantém este agente pode despublicá-lo.',
  'marketplace.archive_requires_maintainer': () => 'Só quem mantém este agente pode descontinuá-lo.',
  'marketplace.restore_requires_maintainer': () => 'Só quem mantém este agente pode trazê-lo de volta.',
  'marketplace.move_requires_maintainer': () => 'Só quem mantém este agente pode movê-lo.',
  'marketplace.audience_requires_maintainer': () => 'Só quem mantém este agente pode escolher quem pode usar.',
  'marketplace.suspend_requires_admin': () => 'Só um administrador pode suspender um agente.',
  'marketplace.unsuspend_requires_admin': () => 'Só um administrador pode retirar uma suspensão.',
  'marketplace.invitation_not_accepted': () => 'Aceite o convite para coeditar este agente antes de alterá-lo.',

  // --- o portão do ciclo de vida ---------------------------------------------

  'marketplace.audience_needs_a_group': () => 'Escolha pelo menos um grupo para compartilhar este agente.',
  'marketplace.publish_blocked_archived': () =>
    'Este agente está descontinuado. Traga-o de volta antes de publicar de novo.',
  'marketplace.publish_blocked_suspended': () =>
    'Este agente está pausado. Um administrador precisa retomá-lo primeiro.',
  'marketplace.publish_needs_a_name': () => 'Dê um nome a este agente antes de publicá-lo.',
  'marketplace.publish_needs_a_saved_version': () => 'Salve este agente antes de publicá-lo.',
  'marketplace.not_published': () => 'Este agente não está publicado.',
  'marketplace.archive_needs_published': () => 'Só um agente publicado pode ser descontinuado.',
  'marketplace.restore_needs_archived': () => 'Só um agente descontinuado pode ser trazido de volta.',
  'marketplace.restore_needs_a_saved_version': () => 'Salve este agente antes de trazê-lo de volta.',
  'marketplace.already_suspended': () => 'Este agente já está suspenso.',
  'marketplace.suspension_needs_reasons': () =>
    'Uma suspensão precisa de um motivo para quem é responsável pelo agente e outro para as pessoas que o usam.',
  'marketplace.not_suspended': () => 'Este agente não está suspenso.',

  // --- nomes (AGM-005) -------------------------------------------------------

  'marketplace.name_reserved': () =>
    'Esse nome é reservado. Escolha outro, ou peça a um administrador para publicá-lo para você.',
  'marketplace.name_taken': () => 'Outro agente já usa esse nome. Escolha um diferente.',
  'marketplace.name_recently_retired': () =>
    'Esse nome era de um agente descontinuado há pouco tempo e ainda está reservado. Escolha outro, ou pergunte a um administrador sobre ele.',
  'marketplace.name_just_taken': () =>
    'Outro agente acabou de ficar com esse nome. Escolha um diferente e publique de novo.',

  // --- o lint de conteúdo (AGM-024) ------------------------------------------

  'marketplace.lint_instructions_empty': () =>
    'Este agente ainda não tem instruções. Descreva o que ele deve fazer e como deve responder — sem elas ele não consegue executar.',
  'marketplace.lint_trust_mark_name': () =>
    'Nomes não podem conter “official”, “verified” nem um símbolo de visto. Só um administrador pode marcar um agente como oficial.',
  'marketplace.lint_trust_mark_tagline': () =>
    'O resumo em uma linha não pode conter “official”, “verified” nem um símbolo de visto. Só um administrador pode marcar um agente como oficial.',
  'marketplace.lint_trust_mark_starter': () =>
    'Perguntas iniciais não podem conter “official”, “verified” nem um símbolo de visto.',
  'marketplace.lint_trust_mark_text': () =>
    'Este texto não pode conter “official”, “verified” nem um símbolo de visto.',
  'marketplace.lint_instructions_short': () =>
    'Estas instruções estão muito curtas. Algumas frases sobre o que o agente faz, para quem ele é e o que ele deve recusar deixam as respostas bem mais consistentes.',
  /**
   * Três frases inteiras, e nenhuma acusação entra como parâmetro. Em inglês a API montava
   * *"they are ${what}"* com um gerúndio pronto; aqui o verbo carrega o objeto junto (*não
   * citar as fontes dele*, *afirmar que ele é uma pessoa*) e a oração ao redor teria de ser
   * remontada palavra a palavra — que é exatamente o que D-20 proíbe.
   */
  'marketplace.lint_contradicts_citations': () =>
    'Estas instruções parecem dizer ao agente para não citar as fontes dele. As regras da plataforma valem sempre, então esta linha não vai ter efeito e pode confundir o agente.',
  'marketplace.lint_contradicts_platform_rules': () =>
    'Estas instruções parecem dizer ao agente para ignorar as regras da plataforma. Essas regras valem sempre, então esta linha não vai ter efeito e pode confundir o agente.',
  'marketplace.lint_contradicts_claims_human': () =>
    'Estas instruções parecem dizer ao agente para afirmar que ele é uma pessoa. As regras da plataforma valem sempre, então esta linha não vai ter efeito e pode confundir o agente.',
  'marketplace.lint_impersonation_name': () =>
    'Este nome dá a entender que o agente fala em nome de uma área. Se não for essa área que está publicando, escolha um nome que diga de quem ele é de verdade.',
  // "O resumo em uma linha" é como as outras entradas já chamam a tagline; vale a pena repetir
  // para que as duas frases sobre o mesmo campo usem a mesma palavra.
  'marketplace.lint_impersonation_tagline': () =>
    'O resumo em uma linha afirma falar em nome da empresa ou de uma área. Em vez disso, descreva com o que o agente ajuda.',

  // --- saúde do agente (AGM-048) ---------------------------------------------
  //
  // Não são recusas: são o que a plataforma descobriu sozinha sobre o agente e conta a quem
  // responde por ele, no painel de saúde e de novo no painel de dados — o mesmo registro nas
  // duas telas, e por isso uma frase só.
  //
  // *base de conhecimento* (f.) manda em toda a concordância da primeira metade (*excluída*,
  // *desligada*, *dela*). Na segunda metade o valor entre aspas é um nome vindo de outro
  // sistema, de gênero desconhecível, e por isso nenhuma frase concorda com ele: quem concorda
  // é *habilidade* (f.), que a própria frase fornece. “Desligou” e “mudou” entram na voz ativa
  // pelo mesmo motivo — verbo não concorda, particípio concorda.

  'marketplace.health_owner_group_unmanaged': () =>
    'Ninguém mais gerencia o grupo dono deste agente, então ninguém pode alterá-lo. Ele continua funcionando; um administrador pode designar uma pessoa gestora ou mover o agente.',
  'marketplace.health_owner_deactivated': () =>
    'A pessoa responsável por este agente não tem mais conta. Ele continua funcionando, mas precisa de outra pessoa responsável — um administrador pode movê-lo.',
  'marketplace.health_agent_unreadable': () =>
    'Não foi possível ler este agente. Abra-o no construtor e salve de novo; se não resolver, um administrador pode analisá-lo.',
  'marketplace.health_agent_cannot_run': () =>
    'Este agente não consegue executar com a configuração atual. Confira se ele ainda tem instruções e salve de novo.',
  'marketplace.health_knowledge_deleted': ({ base }) =>
    `A base de conhecimento “${base}” foi excluída. Este agente não consegue mais responder a partir dela — anexe outra, ou tire essa da lista.`,
  'marketplace.health_knowledge_disabled': ({ base }) =>
    `A base de conhecimento “${base}” foi desligada, então este agente não recebe nada dela.`,
  'marketplace.health_knowledge_unknown': () =>
    'Uma das bases de conhecimento que este agente usa não existe mais. Abra o construtor e confira a lista.',
  'marketplace.health_coverage_gap': ({ base }) =>
    `Algumas pessoas que podem usar este agente não podem usar “${base}”, então as respostas delas ficam sem essa base. Quem gerencia a base pode deixar que ela responda por meio deste agente para todo mundo que ele alcança — peça isso no construtor.`,
  'marketplace.health_builtin_capability_gone': ({ capability }) =>
    `Uma das coisas que este agente podia fazer (“${capability}”) não existe mais.`,
  'marketplace.health_capability_withdrawn': ({ capability }) =>
    `O sistema conectado de onde “${capability}” veio não oferece mais essa habilidade. Este agente não vai conseguir usar.`,
  'marketplace.health_capability_quarantined': ({ capability }) =>
    `“${capability}” mudou, e essa habilidade fica pausada até que um administrador revise. Este agente não vai usar enquanto isso.`,
  'marketplace.health_capability_switched_off': ({ capability }) =>
    `Um administrador desligou “${capability}”, então este agente não pode usar essa habilidade.`,

  // --- cópia (AGM-030) -------------------------------------------------------

  'marketplace.copy_blocked': () => 'Quem é responsável por este agente desligou a cópia.',
  'marketplace.copy_restricted_to_groups': () =>
    'Este agente é compartilhado com grupos específicos, então quem é responsável por ele decide quem pode copiá-lo.',

  // --- mover (D-32) ----------------------------------------------------------

  // "Já pertence a quem você escolheu" é `knowledge.already_owned_there`, sem sujeito para
  // servir aos dois gêneros.
  'marketplace.move_target_not_managed': () =>
    'Você pode movê-lo para um grupo que você gerencia. Um administrador pode movê-lo para qualquer lugar.',

  // --- coedição (AGM-026) ----------------------------------------------------

  'marketplace.editor_already_maintains': () => 'Esta pessoa já mantém o agente.',
  // "coeditora" concorda com "pessoa", que é feminino e vale para qualquer pessoa.
  'marketplace.editor_not_found': () => 'Essa pessoa não é coeditora deste agente.',
  'marketplace.no_open_invitation': () => 'Não há convite aberto para você neste agente.',

  // --- engajamento (AGM-121/123) ---------------------------------------------

  'marketplace.message_not_available': () => 'Nenhuma mensagem com esse id está disponível para você.',
  'marketplace.feedback_needs_an_answer': () => 'O retorno vale para uma resposta, não para a sua própria mensagem.',
  'marketplace.feedback_not_counted_in_preview': () => 'Respostas na prévia do construtor não contam como retorno.',
  'marketplace.report_already_open': () =>
    'Você já denunciou este agente e um administrador ainda não terminou de analisar. Não precisa denunciar de novo.',
  'marketplace.report_not_filed': () => 'Não foi possível registrar a denúncia. Tente de novo.',

  // --- administração ---------------------------------------------------------

  'marketplace.settings_must_be_whole': () => 'Envie o documento de configurações inteiro, não uma parte dele.',
  // O diagnóstico do validador fica em inglês, como um id ou um nome de arquivo (D-17).
  'marketplace.settings_invalid': ({ detail }) => `Estas configurações não são válidas: ${detail}`
}
